/* eslint-disable @typescript-eslint/no-explicit-any */
import isEqual from 'lodash/isEqual'

/**
 * Compact an object, removing all keys which have value that match a set of values
 * @param object the object which should be compacted
 * @param targetValues when a key has one of these values, it will be filtered out
 */
export function compactObjectMatchingValues<ObjectType extends {}>(
  object: ObjectType,
  targetValues: ReadonlyArray<any>
) {
  const defaultResult: Partial<ObjectType> = {}

  return Object.entries(object).reduce((acc, [key, value]) => {
    if (targetValues.some(target => isEqual(value, target))) return acc

    return { ...acc, [key]: value }
  }, defaultResult) as ObjectType
}

/**
 * Compact an object, removing all keys which have value that are undefined
 * @param object the object which should be compacted
 */
export function compactObject<ObjectType extends {}>(object: ObjectType) {
  return compactObjectMatchingValues<ObjectType>(object, [undefined])
}
/**
 * Compact an object, removing all keys which have value that are null or undefined
 * @param object the object which should be compacted
 */
export function compactNullObject<ObjectType extends {}>(object: ObjectType) {
  return compactObjectMatchingValues<ObjectType>(object, [undefined, null])
}

/**
 * Compact an object, removing all keys which have values that are an empty array
 * @param object the object which should be compacted
 */
export function compactEmptyArray<ObjectType extends {}>(object: ObjectType) {
  return compactObjectMatchingValues(object, [[]])
}

/**
 * Compact an object, removing all keys which have values that are an empty array
 * @param object the object which should be compacted
 */
export function compactFalsey<ObjectType extends {}>(object: ObjectType) {
  return compactObjectMatchingValues(object, [false, null, undefined, '', 0])
}
